import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "components/Button";
import { IconClose } from "icons";
import { useBillingPortal } from "hooks/useBillingPortal";
import { useAppSelector } from "store";
import CreditShortfallInfo from "./CreditShortfallInfo";
import { declineMessageKey, payInvoiceUrl } from "utils/paymentProblem";

interface PastDueModalProps {
  onClose: () => void;
  requiredCredits?: number | null;
  remainingCredits?: number | null;
  // STRIPE P5 / PD5: "plan_change" when the modal answers a refused plan / seat change or new-plan checkout.
  reason?: string;
}

const PastDueModal: React.FC<PastDueModalProps> = ({ onClose, requiredCredits = null, remainingCredits = null, reason }) => {
  const { t } = useTranslation();
  // The backend's billing-management verdict; before `GET /users/{id}` lands it is false, which only hides the portal
  // button for a moment — the backend still refuses a member's portal request either way.
  const canManageBilling = useAppSelector((state) => state.common.user.canManageBilling);
  // P7 F2-F1: v3's decline reason and (manager only) the hosted invoice to pay — detail; absent → today's modal.
  const paymentProblem = useAppSelector((state) => state.common.user.paymentProblem);
  const reasonKey = declineMessageKey(paymentProblem);
  const invoiceUrl = payInvoiceUrl(paymentProblem, canManageBilling === true);
  const [error, setError] = React.useState<string | null>(null);
  // STRIPE P6 F-d / N3: the same click-time portal session as Plan & Billing — no `window.open` after an `await` (popup
  // blocked), and an interceptor-handled answer (403 team_suspended raises the suspended screen) opens nothing.
  const { openPortal, isOpening } = useBillingPortal({ onError: setError });

  const handleUpdatePayment = () => {
    setError(null);
    openPortal();
  };

  return (
    <Modal onClose={onClose} show={true} bodyClassName="!border !border-grey-light bg-trueWhite dark:bg-surface-dark !max-w-[480px] !w-full p-5" className="!z-[99999]" backdropDisabled={true}>
      <div className="relative flex flex-col items-center gap-6 w-full">
        <div className="cursor-pointer absolute top-0 right-0 w-fit bg-grey-light rounded-full p-0.5" onClick={onClose}>
          <IconClose />
        </div>

        <div className="flex flex-col items-center gap-2.5 mt-4">
          <h3 className="text-h3 text-center dark:text-white">{t("billing:past_due_modal.title")}</h3>
          <span className="text-bodyMd text-center text-grey">{t("billing:past_due_modal.description")}</span>
          {reasonKey && (
            <span className="text-bodyMd text-center text-trueBlack dark:text-white" data-testid="past-due-decline-reason">
              {t(reasonKey)}
            </span>
          )}
          {reason === "plan_change" && (
            <span className="text-bodyMd text-center text-trueBlack dark:text-white" data-testid="past-due-plan-change-blocked">
              {t("billing:past_due_modal.plan_change_blocked")}
            </span>
          )}
        </div>

        <CreditShortfallInfo requiredCredits={requiredCredits} remainingCredits={remainingCredits} />

        <div className="flex flex-col gap-2 w-full bg-grey-light/30 dark:bg-black-300/50 rounded-lg p-4">
          <span className="text-bodySm text-grey text-center">{t("billing:past_due_modal.footnote_portal")}</span>
        </div>

        {error && <span className="text-bodySm text-orange text-center bg-orange/10 rounded-md px-3 py-2 w-full">{error}</span>}

        {canManageBilling ? (
          <div className="flex flex-col gap-2 w-full">
            <Button className="btn-primary w-full" onClick={handleUpdatePayment} disabled={isOpening} data-testid="past-due-update-payment-btn">
              {isOpening ? t("billing:past_due_modal.cta_loading") : t("billing:past_due_modal.cta")}
            </Button>
            {invoiceUrl && (
              <a href={invoiceUrl} className="btn btn-secondary w-full" data-testid="past-due-pay-invoice">
                {t("billing:past_due_modal.pay_invoice")}
              </a>
            )}
          </div>
        ) : (
          // A member cannot open the team's billing portal — tell them who can fix it instead of a button that fails.
          <span className="text-bodyMd text-center text-trueBlack dark:text-white" data-testid="past-due-contact-admin">
            {t("billing:past_due_modal.member_contact_admin")}
          </span>
        )}
      </div>
    </Modal>
  );
};

export default PastDueModal;
